/**
 * Help Center content model.
 *
 * Articles are plain data so they're easy to edit without touching JSX. Each
 * article body is a list of typed blocks the renderer knows how to draw:
 *   { type: 'p',     text }                     – paragraph
 *   { type: 'h',     text }                     – sub-heading inside an article
 *   { type: 'steps', items: [] }                – numbered steps
 *   { type: 'list',  items: [] }                – bulleted list
 *   { type: 'tip',   text }                     – highlighted callout
 *   { type: 'shot',  src, alt, caption }        – screenshot (from /help/<src>)
 *   { type: 'code',  text }                      – code / embed snippet
 *
 * Screenshots live in  frontend/public/help/  and are referenced by file name.
 * A missing image renders a labelled placeholder, so articles are usable before
 * every screenshot exists.
 */

export const HELP_CATEGORIES = [
  {
    id: 'getting-started',
    title: 'Getting started',
    articles: ['create-account', 'sign-in', 'dashboard-tour'],
  },
  {
    id: 'knowledge',
    title: 'Knowledge base',
    articles: ['upload-knowledge'],
  },
  {
    id: 'site-agent',
    title: 'Your Site Agent',
    articles: ['create-widget', 'configure-widget', 'embed-widget', 'api-keys'],
  },
  {
    id: 'leads',
    title: 'Leads & conversations',
    articles: ['leads-crm', 'conversations', 'analytics'],
  },
  {
    id: 'settings',
    title: 'Settings',
    articles: ['profile', 'appearance', 'notifications', 'security', 'organization', 'plans'],
  },
];

export const HELP_ARTICLES = {
  // ---- Getting started ---------------------------------------------------
  'create-account': {
    title: 'Create your account',
    summary: 'Sign up, verify your email with a 6-digit code, and create your company workspace.',
    body: [
      { type: 'p', text: 'Every SageMindLabs account belongs to a company workspace that is fully isolated from every other company. Creating an account sets up both your login and your workspace.' },
      { type: 'h', text: 'Step 1 — Fill in your details' },
      { type: 'steps', items: [
        'Go to the Get started / Register page.',
        'Enter your name, email, a password, and your company name. The company name is required — it names your private workspace.',
        'Click “Send verification code”.',
      ] },
      { type: 'shot', src: 'register.png', alt: 'Registration form', caption: 'The two-step sign-up. Company name is required.' },
      { type: 'h', text: 'Step 2 — Verify your email' },
      { type: 'steps', items: [
        'Check your inbox for a 6-digit verification code (it expires in 15 minutes).',
        'Enter the code to finish creating your account.',
        'You can request a new code if it expires.',
      ] },
      { type: 'shot', src: 'verify-code.png', alt: 'Enter verification code', caption: 'Enter the 6-digit code we email you.' },
      { type: 'tip', text: 'Didn’t get the code? Check spam, and make sure the address is one your workspace admin has authorized if you’re on a restricted email tier.' },
    ],
  },
  'sign-in': {
    title: 'Sign in',
    summary: 'Log in to your workspace and reset a forgotten password.',
    body: [
      { type: 'steps', items: [
        'Click “Sign in” from the top navigation.',
        'Enter your username/email and password.',
        'You land on your dashboard.',
      ] },
      { type: 'shot', src: 'login.png', alt: 'Sign-in page', caption: 'The sign-in screen.' },
      { type: 'p', text: 'Forgot your password? Use the “Forgot password” link to receive a reset link by email.' },
    ],
  },
  'dashboard-tour': {
    title: 'A tour of your dashboard',
    summary: 'Where everything lives: knowledge base, your Site Agent, leads, conversations, analytics, and settings.',
    body: [
      { type: 'p', text: 'The dashboard is your control center. The left sidebar takes you to each area of the product.' },
      { type: 'shot', src: 'dashboard.png', alt: 'Dashboard overview', caption: 'The dashboard and left navigation.' },
      { type: 'list', items: [
        'Knowledge base — the documents your agent answers from.',
        'Site Agent (Widget settings) — create and embed the agent on your website.',
        'Leads — visitors captured by your agent, with a lightweight CRM.',
        'Conversations — a transcript of every chat.',
        'Analytics — usage and performance.',
        'Settings — profile, appearance, notifications, security, API keys, and your team.',
      ] },
      { type: 'tip', text: 'On a phone, tap the menu button to open the left navigation.' },
    ],
  },

  // ---- Knowledge base ----------------------------------------------------
  'upload-knowledge': {
    title: 'Add knowledge for your agent',
    summary: 'Upload documents so your agent answers from your content instead of generic knowledge.',
    body: [
      { type: 'p', text: 'Your agent is grounded in the documents you provide. The more relevant your knowledge base, the more accurate and on-brand the answers.' },
      { type: 'steps', items: [
        'Open Knowledge base from the sidebar.',
        'Upload your files (product docs, FAQs, policies, notes).',
        'Wait for processing — your content is indexed so the agent can search it.',
        'Ask a test question in the dashboard chat to confirm it’s answering from your content.',
      ] },
      { type: 'shot', src: 'knowledge-base.png', alt: 'Knowledge base page', caption: 'Upload and manage the documents your agent learns from.' },
      { type: 'tip', text: 'If answers seem generic, make sure your documents finished processing and cover the topic you’re asking about.' },
    ],
  },

  // ---- Site Agent --------------------------------------------------------
  'create-widget': {
    title: 'Create your Site Agent',
    summary: 'Create an embeddable agent (widget) you can place on any website.',
    body: [
      { type: 'p', text: 'A Site Agent is your AI agent, deployable on any website as a chat widget that answers visitors 24/7 from your knowledge base.' },
      { type: 'steps', items: [
        'Go to Settings → Widget.',
        'Click “Create Widget”.',
        'Give it a name and company name, then save.',
      ] },
      { type: 'shot', src: 'widget-list.png', alt: 'Widget settings list', caption: 'Your Site Agents. Create, edit, generate the embed, or delete.' },
    ],
  },
  'configure-widget': {
    title: 'Configure appearance & behavior',
    summary: 'Set the theme, colors, position, welcome message, and behavior options for your agent.',
    body: [
      { type: 'p', text: 'Open a widget and click Edit to customize how it looks and behaves on your site.' },
      { type: 'shot', src: 'widget-create.png', alt: 'Widget configuration modal', caption: 'Appearance, bot configuration, behavior, and allowed domains.' },
      { type: 'h', text: 'Appearance' },
      { type: 'list', items: [
        'Theme (auto / light / dark), primary color, and on-screen position.',
        'Widget size and bot name.',
        'Welcome message shown when the agent opens.',
      ] },
      { type: 'h', text: 'Behavior' },
      { type: 'list', items: [
        'Auto-open on page load, typing indicator, require visitor info.',
        'Enable file uploads or voice input.',
        'Rate limit (messages per minute) to protect against abuse.',
      ] },
      { type: 'h', text: 'Allowed domains' },
      { type: 'p', text: 'Add the domains where the agent is allowed to run. Leave empty to allow anywhere while testing.' },
      { type: 'tip', text: 'Remember to Save. Changes apply to the embedded agent immediately.' },
    ],
  },
  'embed-widget': {
    title: 'Embed the agent on your website',
    summary: 'Copy the snippet, add your API key, and paste it into your site.',
    body: [
      { type: 'p', text: 'From the widget list, click “Script” to get the embed snippet, then paste it before the closing </body> tag of your website.' },
      { type: 'shot', src: 'widget-embed.png', alt: 'Widget embed script', caption: 'The embed snippet for your website.' },
      { type: 'p', text: 'Set your API key in the snippet (see the API keys article), and confirm the chatbot id and API URL. A minimal embed looks like this:' },
      { type: 'code', text: '<script>\n  window.chtlConfig = {\n    chatbotId: "cb_xxxxxxxxxxxxxxxx",\n    apiKey: "sm_your_api_key",\n    apiUrl: "https://app.sagemindlabs.com"\n  };\n</script>\n<link rel="stylesheet" href="https://app.sagemindlabs.com/static/widget/wt-dsm-widget.css">\n<script src="https://app.sagemindlabs.com/static/widget/wt-dsm-widget-secure.js" async></script>' },
      { type: 'tip', text: 'The agent must be embedded on a real http(s) page. Opening the HTML file directly from disk (file://) will be blocked by the browser.' },
    ],
  },
  'api-keys': {
    title: 'API keys',
    summary: 'Create a company API key and use it to authenticate your embedded agent.',
    body: [
      { type: 'p', text: 'Your embedded agent authenticates with a company API key. Create one in Settings → API Keys.' },
      { type: 'steps', items: [
        'Go to Settings → API Keys.',
        'Create a new key and copy it — it starts with “sm_” and is shown only once.',
        'Paste it as the apiKey value in your embed snippet.',
      ] },
      { type: 'shot', src: 'api-keys.png', alt: 'API keys settings', caption: 'Create, view, and revoke company API keys.' },
      { type: 'tip', text: 'Treat API keys like passwords. Revoke a key any time from this screen; the embed using it stops working immediately.' },
    ],
  },

  // ---- Leads & conversations --------------------------------------------
  'leads-crm': {
    title: 'Leads & mini-CRM',
    summary: 'Every visitor your agent captures becomes a lead you can track and follow up.',
    body: [
      { type: 'p', text: 'When a visitor shares their details with your agent, they’re captured as a lead with a lightweight CRM: status, notes, and an activity history.' },
      { type: 'shot', src: 'leads.png', alt: 'Leads page', caption: 'Captured leads with status and activity.' },
      { type: 'list', items: [
        'Filter and review leads by status.',
        'Add notes and log activities.',
        'Follow up by email or phone.',
      ] },
    ],
  },
  'conversations': {
    title: 'Conversations',
    summary: 'Read the full transcript of every chat your agent has had.',
    body: [
      { type: 'p', text: 'The Conversations area shows every widget chat so you can see exactly what visitors asked and how your agent answered.' },
      { type: 'shot', src: 'conversations.png', alt: 'Conversations page', caption: 'Full chat transcripts.' },
    ],
  },
  'analytics': {
    title: 'Analytics',
    summary: 'Track usage and performance over time.',
    body: [
      { type: 'p', text: 'Analytics summarizes activity — conversations, engagement, and trends — so you can see how your agent is performing.' },
      { type: 'shot', src: 'analytics.png', alt: 'Analytics dashboard', caption: 'Usage and performance analytics.' },
    ],
  },

  // ---- Settings ----------------------------------------------------------
  'profile': {
    title: 'Profile',
    summary: 'Manage your name, email, and account details.',
    body: [
      { type: 'p', text: 'Open Settings → Profile to view and update your account details.' },
      { type: 'shot', src: 'settings-profile.png', alt: 'Profile settings', caption: 'Your account profile.' },
    ],
  },
  'appearance': {
    title: 'Appearance & themes',
    summary: 'Pick the dashboard theme that suits you.',
    body: [
      { type: 'p', text: 'Settings → Appearance lets you choose the dashboard theme. Your choice is remembered on this device.' },
      { type: 'shot', src: 'settings-appearance.png', alt: 'Appearance settings', caption: 'Choose your dashboard theme.' },
    ],
  },
  'notifications': {
    title: 'New-chat alerts (email & SMS)',
    summary: 'Get notified by email or SMS when a visitor starts a new chat. Off by default.',
    body: [
      { type: 'p', text: 'Settings → Notifications lets you opt in to email and/or SMS alerts whenever a visitor starts a new chat through your agent. Both are off by default.' },
      { type: 'shot', src: 'notifications.png', alt: 'Notification settings', caption: 'Opt in to email/SMS new-chat alerts and set caps.' },
      { type: 'list', items: [
        'Email alerts — sends to the address you specify.',
        'SMS alerts — texts a phone number (carrier charges may apply).',
        'Daily cap and minimum interval — protect against alert spikes.',
      ] },
      { type: 'tip', text: 'The toggles are available once your admin has configured an email/SMS provider on the server.' },
    ],
  },
  'security': {
    title: 'Security & sessions',
    summary: 'Review active sessions and manage your account security.',
    body: [
      { type: 'p', text: 'Settings → Security lets you review active sessions and sign out where needed.' },
      { type: 'shot', src: 'security.png', alt: 'Security settings', caption: 'Manage sessions and security.' },
    ],
  },
  'organization': {
    title: 'Your team',
    summary: 'Invite team members and manage roles within your company workspace.',
    body: [
      { type: 'p', text: 'Settings → Organization is where you manage your company workspace and team members. Access is scoped to your company only.' },
      { type: 'shot', src: 'organization.png', alt: 'Organization settings', caption: 'Manage your team and roles.' },
      { type: 'tip', text: 'Team-member and widget limits depend on your plan. See the plans article.' },
    ],
  },
  'plans': {
    title: 'Plans & limits',
    summary: 'What’s included on Free, Pro, and Enterprise.',
    body: [
      { type: 'list', items: [
        'Free — 2 team members and 1 Site Agent.',
        'Pro ($15/mo) — more members and agents, plus additional features.',
        'Enterprise — request a meeting to discuss your needs.',
      ] },
      { type: 'p', text: 'See the Pricing page for the full comparison. A platform admin activates paid plans.' },
    ],
  },
};
